import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CMSHeroSection } from "../../../../services/cms";
import ImageUpload from "../../../../components/ImageUpload";

interface HeroSectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editingHero: CMSHeroSection | null;
  setEditingHero: React.Dispatch<React.SetStateAction<CMSHeroSection | null>>;
}

const HeroSectionDialog: React.FC<HeroSectionDialogProps> = ({
  open,
  onClose,
  onSave,
  editingHero,
  setEditingHero,
}) => {
  const { t } = useTranslation();

  const handleChange = (field: keyof CMSHeroSection, value: any) => {
    if (!editingHero) return;
    setEditingHero({
      ...editingHero,
      [field]: value,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          width: { xs: "95%", sm: "90%", md: "80%" },
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
        {editingHero?.id
          ? t("admin.contentManagement.editHeroSection")
          : t("admin.contentManagement.addHeroSection")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label={t("admin.contentManagement.fields.title")}
            fullWidth
            value={editingHero?.title || ""}
            onChange={(e) => handleChange("title", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.subtitle")}
            fullWidth
            value={editingHero?.subtitle || ""}
            onChange={(e) => handleChange("subtitle", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.ctaText")}
            fullWidth
            value={editingHero?.cta_text || ""}
            onChange={(e) => handleChange("cta_text", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.ctaLink")}
            fullWidth
            value={editingHero?.cta_link || ""}
            onChange={(e) => handleChange("cta_link", e.target.value)}
          />
          <ImageUpload
            currentImage={editingHero?.background_image || ""}
            onImageChange={(url: string) => handleChange("background_image", url)}
            folder="hero"
            label={t("admin.contentManagement.fields.backgroundImage")}
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label={t("admin.contentManagement.fields.gradientStartColor")}
              fullWidth
              type="color"
              value={editingHero?.gradient_start || "#00d4ff"}
              onChange={(e) => handleChange("gradient_start", e.target.value)}
            />
            <TextField
              label={t("admin.contentManagement.fields.gradientEndColor")}
              fullWidth
              type="color"
              value={editingHero?.gradient_end || "#ff0080"}
              onChange={(e) => handleChange("gradient_end", e.target.value)}
            />
          </Box>
          <TextField
            label={t("admin.contentManagement.fields.displayOrder")}
            fullWidth
            type="number"
            value={editingHero?.display_order || 0}
            onChange={(e) => handleChange("display_order", parseInt(e.target.value))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editingHero?.is_active ?? true}
                onChange={(e) => handleChange("is_active", e.target.checked)}
              />
            }
            label={t("admin.contentManagement.active")}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>
          {t("admin.contentManagement.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          sx={{
            background: "linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)",
          }}
        >
          {t("admin.contentManagement.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HeroSectionDialog;